import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

import { Action, Dispatch } from 'redux';
import { actions } from 'react-redux-form';
import { AppState } from '..';
import { contractService } from '@services/ContractService';
import {
  ContractViewModel,
  ContractDetailsViewModel,
  PropertyTypeViewModel,
  NoRenewViewModel,
  ContractMetaDataViewModel,
  ContractSimpleViewModel
} from '@models/Contract';
import { OptionsViewModel, PageViewModel, AppDispatcher } from '@models/common.models';
import { CommentViewModel } from '@models/Comment';
import { customerService } from '@services/CustomerService';
import { CustomerViewModel } from '@models/Customer';
import { realEstateOfficesService } from '@services/RealEstateOfficesService';
import { realEstateAgentsService } from '@services/RealEstateAgentsService';
import { RealEstateAgentViewModel } from '@models/RealEstateAgent';
import { RealEstateOfficeViewModel } from '@models/RealEstateOffice';

import { escrowService } from '@services/EscrowService';
import { EscrowOfficeViewModel } from '@models/EscrowOffice';
import { EscrowOfficerViewModel } from '@models/EscrowOfficer';
import { transactionCoordinatorService } from '@services/TransactionCoordinatorService';
import { TransactionCoordinatorViewModel } from '@models/TransactionCoordinator';
import { ContractState } from './contract.reducer';
import { QuoteViewModel } from '@models/Quote';
import { documentService } from '@services/DocumentService';
import { DocumentViewModel, FileViewModel } from '@models/Document';
import {
  ContractCancellationModel,
  ContractCancellationNotificationModel,
  ContractPaymentInfoModel,
  ContractOrderCancellationModel
} from '@models/ContractCancellation';
import { propertyManagementService } from '@services/PropertyManagementService';
import { PropertyManagerSearchViewModel, PropertyManagerViewModel } from '@models/PropertyManager';
import { ProductViewModel, PriceAdjustmentViewModel, ProductCoverageViewModel } from '@models/Product';
import { getCustomerFromService } from '../customer/customer.actions';
import { getById, requestAction, setFileListLoading } from '@common/components/common.actions';
import { getPropertyManagerFromService } from '../property-manager/property-manager.actions';
import { convertQuotePreventReset } from '../quote/quote.actions';
import {
  clearCoverageStore,
  loadCoverageState,
  clearProductChannelSelection,
  saveToContractFlow,
  getEmployeeDiscount
} from './_components/coverage/_actions/coverage.actions';
import { PaymentViewModel } from '@models/Payment';
import { initialCoverageState, CoverageState } from './_components/coverage/coverage.reducer';
import { ProductGroupViewModel, ProductChannelViewModel } from '@models/Product';
import { resetPaymentState } from '../payment/payment.actions';
import { pullCoverageStateFromEntity, pullPropertyInfoFromContractDetails } from './contract.helpers';
import FormNames from '@constants/FormNames.enum';
import { PropertyInfoForm } from '@models/forms/PropertyInfoForm';
import { searchContractTerm } from 'src/contract/contract-history.actions';
import { sendDefaultConfirmationEmails } from 'src/contract/_components/contract-email/contract-email.actions';
import { ContractOrderType } from '@models/Contract';
import { ContractSearchViewModel } from '@models/ServiceWorkOrder';
import { productService } from '@services/ProductService';

/*
These are the actions for the page.
Think of these as 'event emitters' used by the components on the page.
*/

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum ContractActionType {
  /************ Save Property Info  *************/
  SaveOrderType = 'SaveOrderType',
  SavePropertyInfo = 'SavePropertyInfo',
  SaveAffiliatesInfo = 'SaveAffiliatesInfo',
  SaveCustomerInfo = 'SaveCustomerInfo',
  SaveEscrowInfo = 'SaveEscrowInfo',
  SaveCoeDate = 'SaveCoeDate',

  SaveCoverageStep = 'SaveCoverageStep',

  SaveContractStart = 'SaveContractStart',
  SaveContractSuccess = 'SaveContractSuccess',
  SaveContractFailure = 'SaveContractFailure',

  SavePaymentInfoStart = 'SavePaymentInfoStart',
  SavePaymentInfoSuccess = 'SavePaymentInfoSuccess',
  SavePaymentInfoFailure = 'SavePaymentInfoFailure',

  /************* Get  **************/
  GetContactInitiatorsStart = 'ContactInitiatorsGetStart',
  GetContactInitiatorsSuccess = 'ContactInitiatorsGetSuccess',
  GetContactInitiatorsFailure = 'ContactInitiatorsGetFailure',
  GetContactMediumsStart = 'ContactMediumsGetStart',
  GetContactMediumsSuccess = 'ContactMediumsGetSuccess',
  GetContactMediumsFailure = 'ContactMediumsGetFailure',
  GetContactTypesStart = 'ContactTypesGetStart',
  GetContactTypesSuccess = 'ContactTypesGetSuccess',
  GetContactTypesFailure = 'ContactTypesGetFailure',
  GetPropertyTypesStart = 'PropertyTypesGetStart',
  GetPropertyTypesSuccess = 'PropertyTypesGetSuccess',
  GetPropertyTypesFailure = 'PropertyTypesGetFailure',
  GetPropertySaleTypesStart = 'PropertySaleTypesGetStart',
  GetPropertySaleTypesSuccess = 'PropertySaleTypesGetSuccess',
  GetPropertySaleTypesFailure = 'PropertySaleTypesGetFailure',
  GetContractDetailsStart = 'ContractDetailsGetStart',
  GetContractDetailsSuccess = 'ContractDetailsGetSuccess',
  GetContractDetailsFailure = 'ContractDetailsGetFailure',
  ClearContractDetails = 'ClearContractDetails',
  GetContractStart = 'GetContractStart',
  GetContractSuccess = 'GetContractSuccess',
  GetContractFailure = 'GetContractFailure',

  GetBillingOptionsStart = 'BillingOptionsGetStart',
  GetBillingOptionsSuccess = 'BillingOptionsGetSuccess',
  GetBillingOptionsFailure = 'BillingOptionsGetFailure',

  SelectPriceAdjustment = 'PriceAdjustmentSelect',
  SetPriceAdjustmentAmount = 'PriceAdjustmentAmountSet',

  GetContractCancellationReasonsStart = 'GetContractCancellationReasonsStart',
  GetContractCancellationReasonsSuccess = 'GetContractCancellationReasonsSuccess',
  GetContractCancellationReasonsFailure = 'GetContractCancellationReasonsFailure',
  GetContractCancellationNotificationsStart = 'GetContractCancellationNotificationsStart',
  GetContractCancellationNotificationsSuccess = 'GetContractCancellationNotificationsSuccess',
  GetContractCancellationNotificationsFailure = 'GetContractCancellationNotificationsFailure',

  /*** Loading Agents Info ****/
  ContractLoadRealEstateAgentsStart = 'ContractLoadRealEstateAgentStart',
  ContractLoadRealEstateAgentsFailure = 'ContractLoadRealEstateAgentsFailure',
  LoadInitiatingAgentSuccess = 'LoadInitiatingAgentSuccess',
  LoadCooperatingAgentSuccess = 'LoadCooperatingAgentSuccess',
  LoadRealEstateAgentSuccess = 'LoadRealEstateAgentSuccess',

  LoadEscrowOfficer = 'LoadEscrowOfficer',
  LoadTransactionCoordinator = 'LoadTransactionCoordinator',
  GetAssetManagersStart = 'AssetManagersGetStart',
  GetAssetManagersSuccess = 'AssetManagersGetSuccess',
  GetAssetManagersFailure = 'AssetManagersGetFailure',
  LoadBuyerCustomer = 'LoadBuyerCustomer',
  LoadSellerCustomer = 'LoadSellerCustomer',
  LoadPropertyManager = 'LoadPropertyManager',
  LoadEscrowOffice = 'LoadEscrowOffice',

  /************ Save Customer Info  *************/
  SaveCustomerInfoStart = 'SaveCustomerInfoStart',
  SaveCustomerInfoSuccess = 'SaveCustomerInfoSuccess',
  SaveCustomerInfoFailure = 'SaveCustomerInfoFailure',

  ClearBuyer = 'ClearBuyer',
  ClearSeller = 'ClearSeller',
  ClearPropertyManager = 'ClearPropertyManager',
  ClearInitAgent = 'ClearInitAgent',
  ClearCoopAgent = 'ClearCoopAgent',
  ClearEscrowOfficer = 'ClearEscrowOfficer',
  ClearTransactionCoordinator = 'ClearTransactionCoordinator',

  SavePaymentInfo = 'SavePaymentInfo',

  AddNewTxC = 'AddNewTxC',
  RemoveTxC = 'RemoveTxC',

  RemoveRealEstateAgent = 'RemoveRealEstateAgent',

  /********** SearchCommentsByContract **********/
  SearchCommentsByContractInit = 'SearchCommentsByContractInit',
  SearchCommentsByContractSuccess = 'SearchCommentsByContractSuccess',
  SearchCommentsByContractFailure = 'SearchCommentsByContractFailure',

  SelectComment = 'SelectComment',

  // Convert Quote to Contract
  ConvertQuoteToContract = 'ConvertQuoteToContract',

  /********** SaveContractFiles **********/
  SaveContractFilesStart = 'SaveContractFilesStart',
  SaveContractFilesSuccess = 'SaveContractFilesSuccess',
  SaveContractFilesFailure = 'SaveContractFilesFailure',

  /********** RemoveContractFile **********/
  RemoveContractFileStart = 'RemoveContractFileStart',
  RemoveContractFileSuccess = 'RemoveContractFileSuccess',
  RemoveContractFileFailure = 'RemoveContractFileFailure',

  /********** GetContractFiles **********/
  GetContractFilesStart = 'GetContractFilesStart',
  GetContractFilesSuccess = 'GetContractFilesSuccess',
  GetContractFilesFailure = 'GetContractFilesFailure',

  /********** GetContractBalances **********/
  GetContractBalancesStart = 'GetContractBalancesStart',
  GetContractBalancesSuccess = 'GetContractBalancesSuccess',
  GetContractBalancesFailure = 'GetContractBalancesFailure',
  SelectBalance = 'SelectBalance',

  /********** GetEdocTypes **********/
  GetEdocTypesStart = 'GetEdocTypesStart',
  GetEdocTypesSuccess = 'GetEdocTypesSuccess',
  GetEdocTypesFailure = 'GetEdocTypesFailure',

  /*** Cancel Contract Order ******/
  CancelContractOrderStart = 'CancelContractOrderStart',
  CancelContractOrderSuccess = 'CancelContractOrderSuccess',
  CancelContractOrderFailure = 'CancelContractOrderFailure',

  /**** Contract Cancellation  ******/
  ContractCancellationStart = 'ContractCancellationStart',
  ContractCancellationSuccess = 'ContractCancellationSuccess',
  ContractCancellationFailure = 'ContractCancellationFailure',

  /**** Calc Contract Cancellation Refund ****/
  CalculateContractCancellationRefundStart = 'CalculateContractCancellationRefundStart',
  CalculateContractCancellationRefundSuccess = 'CalculateContractCancellationRefundSuccess',
  CalculateContractCancellationRefundFailure = 'CalculateContractCancellationRefundFailure',

  AddEmailNotification = 'AddEmailNotification',
  RemoveEmailNotification = 'RemoveEmailNotification',

  SetOverride = 'SetOverride',
  SetRefundByCheck = 'SetRefundByCheck',
  ContractCancellationFormReset = 'ContractCancellationFormReset',

  /**** Update Contract ********/
  UpdateContractStart = 'UpdateContractStart',
  UpdateContractSuccess = 'UpdateContractSuccess',
  UpdateContractFailure = 'UpdateContractFailure',

  /**** Get Renewal Contract ********/
  GetRenewalContractStart = 'RenewalContractGetStart',
  GetRenewalContractSuccess = 'RenewalContractGetSuccess',
  GetRenewalContractFailure = 'RenewalContractGetFailure',
  GetRenewalOptionsStart = 'GetRenewalOptionsStart',
  GetRenewalOptionsSuccess = 'GetRenewalOptionsSuccess',
  GetRenewalOptionsFailure = 'GetRenewalOptionsFailure',

  // /**** Get Renewal Change Fee ********/
  // GetRenewalChangeFeeStart = 'RenewalChangeFeeGetStart',
  // GetRenewalChangeFeeSuccess = 'RenewalChangeFeeGetSuccess',
  // GetRenewalChangeFeeFailure = 'RenewalChangeFeeGetFailure',

  /**** Send Renewal Notice ********/
  SendRenewalNoticeStart = 'RenewalNoticeSendStart',
  SendRenewalNoticeSuccess = 'RenewalNoticeSendSuccess',
  SendRenewalNoticeFailure = 'RenewalNoticeSendFailure',

  /**** Activate ********/
  ActivateContractFailure = 'ActivateContractFailure',
  ActivateContractStart = 'ActivateContractStart',
  ActivateContractSuccess = 'ActivateContractSuccess',

  SetPaymentInfo = 'SetPaymentInfo',
  SetCancellationRefund = 'SetCancellationRefund',

  /**** Contract NoRenew  ******/
  ContractNoRenewStart = 'ContractNoRenewStart',
  ContractNoRenewSuccess = 'ContractNoRenewSuccess',
  ContractNoRenewFailure = 'ContractNoRenewFailure',

  /**** Get Contract NoRenew Reasons  ******/
  GetContractNoRenewReasonsStart = 'GetContractNoRenewReasonsStart',
  GetContractNoRenewReasonsSuccess = 'GetContractNoRenewReasonsSuccess',
  GetContractNoRenewReasonsFailure = 'GetContractNoRenewReasonsFailure',

  /**** Remove NoRenew  ******/
  ContractRemoveNoRenewStart = 'ContractRemoveNoRenewStart',
  ContractRemoveNoRenewSuccess = 'ContractRemoveNoRenewSuccess',
  ContractRemoveNoRenewFailure = 'ContractRemoveNoRenewFailure',

  ResetContractState = 'ResetContractState',

  /*** Suspend Contract */
  SuspendContract = 'SuspendContract',
  ResumeContract = 'ResumeContract',

  /*** Verify Contract Info */
  Verify = 'Verify',

  SetContractMetadata = 'SetContractMetadata',
  /*** Clone */
  LoadAffiliatesWorkflow = 'LoadAffiliatesWorkflow',
  LoadPropertyWorkFlow = 'LoadPropertyWorkflow',
  LoadCustomerWorkFlow = 'LoadCustomerWorkflow',
  LoadPriceAdjustmentWorkflow = 'LoadPriceAdjustmentWorkflow',
  SetContractId = 'SetContractId',
  LoadOtherContractInfo = 'LoadOtherContractInfo',
  SetIsOpenContract = 'SetIsOpenContract',

  /********** Search **********/
  SearchContractsInit = 'SearchContractsInit',
  SearchContractsSuccess = 'SearchContractsSuccess',
  SearchContractsFailure = 'SearchContractsFailure',

  /********** SearchRelatedContracts **********/
  SearchRelatedContractsInit = 'SearchRelatedContractsInit',
  SearchRelatedContractsSuccess = 'SearchRelatedContractsSuccess',
  SearchRelatedContractsFailure = 'SearchRelatedContractsFailure',

  /********** SavingUSPS **********/
  SavingUSPS = 'SavingUSPS',

  SetShouldClear = 'SetShouldClear'
}

export enum ContractRealEstateAgentType {
  RealEstateAgent = 0,
  InitiatingAgent = 1,
  CooperatingAgent = 2
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.
interface ContractAction extends Action {
  readonly type: ContractActionType;
}

export interface LoadOtherContractInfoAction extends ContractAction {
  readonly type: ContractActionType.LoadOtherContractInfo;
  readonly totalPrice: number;
  readonly id: number;
}

export interface LoadAffiliatesWorkflowAction extends ContractAction {
  readonly type: ContractActionType.LoadAffiliatesWorkflow;
  readonly initAgent: RealEstateAgentViewModel;
  readonly coopAgent: RealEstateAgentViewModel;
  readonly initOffice: RealEstateOfficeViewModel;
  readonly coopOffice: RealEstateOfficeViewModel;
  readonly additionalAgents: RealEstateAgentViewModel[];
  readonly propertyManager: PropertyManagerViewModel;

  readonly escrowComments: string;
  readonly escrowOfficer: EscrowOfficerViewModel;
  readonly escrowOffice: EscrowOfficeViewModel;
  readonly transactionCoordinatorIds: Array<TransactionCoordinatorViewModel>;
  readonly additionalRealEstateAgentIds: Array<number>;
  readonly assetManagers: OptionsViewModel[];

  readonly escrowOneTimeFax: string;
  readonly txCOneTimeFax: string;
  readonly fileNumber: string;
  readonly estimatedCoeDate: Date;
}

export interface SaveOrderTypeAction extends ContractAction {
  readonly orderType: ContractOrderType;
}
export interface SavePropertyInfoAction extends ContractAction {
  readonly contractVM: ContractViewModel;
}

export interface SaveAffiliatesInfoAction extends ContractAction {
  readonly contractVM: ContractViewModel;
}

export interface SaveCustomerInfoAction extends ContractAction {
  readonly contractVM: ContractViewModel;
}

export interface SaveEscrowInfoAction extends ContractAction {
  readonly contractVM: ContractViewModel;
}

export interface SaveCoeDateAction extends ContractAction {
  readonly coeDate: Date;
}

export interface SaveContractStartAction extends ContractAction {
  readonly message: string;
}

export interface SaveContractSuccessAction extends ContractAction {
  readonly message: string;
  readonly contractId: number;
}

export interface SaveContractFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface SavePaymentInfoStartAction extends ContractAction {
  readonly message: string;
}

export interface SavePaymentInfoSuccessAction extends ContractAction {
  readonly firstBilling?: OptionsViewModel;
  readonly laterBills?: OptionsViewModel;
  readonly message: string;
  readonly contractId: number;
}

export interface SavePaymentInfoFailureAction extends ContractAction {
  readonly errorMessage: string;
}

/******** Get ***********/
export interface GetContractDetailsStartAction extends ContractAction {}
export interface GetContractDetailsSuccessAction extends ContractAction {
  readonly contractDetailsVM: ContractDetailsViewModel;
}
export interface GetContractDetailsFailureAction extends ContractAction {
  readonly errorMessage: string;
}
export interface ClearContractDetailsAction extends ContractAction {
  readonly type: ContractActionType.ClearContractDetails;
}

export interface GetContractStartAction extends ContractAction {
  type: ContractActionType.GetContractStart;
}

export interface GetContractSuccessAction extends ContractAction {
  readonly type: ContractActionType.GetContractSuccess;
  readonly contractVM: ContractViewModel;
}

export interface GetContractFailureAction extends ContractAction {
  readonly type: ContractActionType.GetContractFailure;
  readonly errorMessage: string;
}

export interface GetContactInitiatorsStartAction extends ContractAction {}

export interface GetContactInitiatorsSuccessAction extends ContractAction {
  readonly contactInitiators: Array<OptionsViewModel>;
}

export interface GetContactInitiatorsFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface GetContactMediumsStartAction extends ContractAction {}

export interface GetContactMediumsSuccessAction extends ContractAction {
  readonly contactMediums: Array<OptionsViewModel>;
}

export interface GetContactMediumsFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface GetContactTypesStartAction extends ContractAction {}

export interface GetContactTypesSuccessAction extends ContractAction {
  readonly contactTypes: Array<OptionsViewModel>;
}

export interface GetContactTypesFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface GetPropertyTypesStartAction extends ContractAction {}

export interface GetPropertyTypesSuccessAction extends ContractAction {
  readonly propertyTypes: Array<PropertyTypeViewModel>;
}

export interface GetPropertyTypesFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface GetPropertySaleTypesStartAction extends ContractAction {}

export interface GetPropertySaleTypesSuccessAction extends ContractAction {
  readonly propertySaleTypes: Array<OptionsViewModel>;
}

export interface GetPropertySaleTypesFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface GetBillingOptionsStartAction extends ContractAction {}

export interface GetBillingOptionsSuccessAction extends ContractAction {
  readonly billingOptions: Array<OptionsViewModel>;
}

export interface GetBillingOptionsFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface GetContractCancellationNotificationsStartAction extends ContractAction {}

export interface GetContractCancellationNotificationsSuccessAction extends ContractAction {
  readonly cancellationNotifications: Array<ContractCancellationNotificationModel>;
}

export interface GetContractCancellationNotificationsFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface GeneralLoadRealEstateAgentsAction extends ContractAction {
  readonly agentType: ContractRealEstateAgentType;
  readonly agentId: number;
}

export interface LoadInitiatingAgentSuccessAction extends ContractAction {
  readonly initAgent: RealEstateAgentViewModel;
  readonly initOffice: RealEstateOfficeViewModel;
}
export interface LoadCooperatingAgentSuccessAction extends ContractAction {
  readonly coopAgent: RealEstateAgentViewModel;
  readonly coopOffice: RealEstateOfficeViewModel;
}
export interface LoadRealEstateAgentSuccessAction extends ContractAction {
  readonly realEstateAgent: RealEstateAgentViewModel;
}

export interface LoadPropertyManagerAction extends ContractAction {
  readonly propertyManager: PropertyManagerSearchViewModel;
}

export interface LoadEscrowOfficerAction extends ContractAction {
  readonly escrowOfficer: EscrowOfficerViewModel;
}

export interface LoadTransactionCoordinatorAction extends ContractAction {
  readonly transactionCoordinator: TransactionCoordinatorViewModel;
}

export interface GetAssetManagersStartAction extends ContractAction {}

export interface GetAssetManagersSuccessAction extends ContractAction {
  readonly assetManagers: Array<OptionsViewModel>;
}

export interface GetAssetManagersFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface LoadBuyerCustomerAction extends ContractAction {
  readonly buyerCustomer: CustomerViewModel;
}
export interface LoadSellerCustomerAction extends ContractAction {
  readonly sellerCustomer: CustomerViewModel;
}

export interface LoadEscrowOfficeAction extends ContractAction {
  readonly escrowOffice: EscrowOfficeViewModel;
}

export interface ClearBuyerAction extends ContractAction {}

export interface ClearSellerAction extends ContractAction {}

export interface ClearPropertyManagerAction extends ContractAction {}

export interface ClearInitAgentAction extends ContractAction {}

export interface ClearCoopAgentAction extends ContractAction {}

export interface ClearEscrowOfficerAction extends ContractAction {}

export interface ClearTransactionCoordinatorAction extends ContractAction {}

export interface AddNewTxCAction extends ContractAction {}

export interface RemoveTxCAction extends ContractAction {
  readonly id: number;
}

export interface LoadPropertyWorkflowAction extends ContractAction {
  readonly propertyInfo: PropertyInfoForm;
}

export interface LoadCustomerWorkflowAction extends ContractAction {
  readonly type: ContractActionType.LoadCustomerWorkFlow;
  readonly buyerId: number;
  readonly sellerId: number;
  readonly propertyManagerId: number;
}

export interface LoadPriceAdjustmentWorkflowAction extends ContractAction {
  readonly priceAdjustment: PriceAdjustmentViewModel;
  readonly priceAdjustmentAmount: number;
  readonly priceAdjustmentNotes: string;
}

export interface RemoveRealEstateAgentAction extends ContractAction {
  readonly index: number;
  readonly all?: boolean;
}

/********** SearchCommentsByContract **********/
export interface SearchCommentsByContractInitAction extends ContractAction {
  readonly contractId: number;
}
export interface SearchCommentsByContractSuccessAction extends ContractAction {
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly searchResult: PageViewModel<CommentViewModel>;
}
export interface SearchCommentsByContractFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface SelectCommentAction extends ContractAction {
  readonly commentText: string;
}

export interface ConvertQuoteToContractAction extends ContractAction {
  readonly quote: QuoteViewModel;
}

export interface GetContractCancellationReasonsStartAction extends ContractAction {}

export interface GetContractCancellationReasonsSuccessAction extends ContractAction {
  readonly reasons: Array<OptionsViewModel>;
}

export interface GetContractCancellationReasonsFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface SetPaymentInfoAction extends ContractAction {
  readonly type: ContractActionType.SetPaymentInfo;
  readonly paymentInfo: ContractPaymentInfoModel;
}

export interface SetCancellationRefundAction extends ContractAction {
  readonly type: ContractActionType.SetCancellationRefund;
  readonly refundInfo: ContractCancellationModel;
}

/********** SaveContractFiles **********/
export interface SaveContractFilesStartAction extends ContractAction {}
export interface SaveContractFilesSuccessAction extends ContractAction {
  readonly contractId: number;
  readonly documents: DocumentViewModel[];
}
export interface SaveContractFilesFailureAction extends ContractAction {
  readonly errorMessage: string;
}

/********** RemoveContractFile **********/
export interface RemoveContractFileStartAction extends ContractAction {}
export interface RemoveContractFileSuccessAction extends ContractAction {
  readonly contractId: number;
  readonly fileId: number;
}
export interface RemoveContractFileFailureAction extends ContractAction {
  readonly errorMessage: string;
}

/********** GetContractFiles **********/
export interface GetContractFilesStartAction extends ContractAction {}
export interface GetContractFilesSuccessAction extends ContractAction {
  readonly contractId: number;
  readonly documents: DocumentViewModel[];
}
export interface GetContractFilesFailureAction extends ContractAction {
  readonly errorMessage: string;
}

/********** GetEdocTypes **********/
export interface GetEdocTypesStartAction extends ContractAction {}
export interface GetEdocTypesSuccessAction extends ContractAction {
  readonly edocTypes: Array<OptionsViewModel>;
}
export interface GetEdocTypesFailureAction extends ContractAction {
  readonly errorMessage: string;
}

/**** Cancel Contract ****/
export interface CancelContractOrderStartAction extends ContractAction {}

export interface CancelContractOrderSuccessAction extends ContractAction {
  readonly message: string;
}

export interface CancelContractOrderFailureAction extends ContractAction {
  readonly errorMessage: string;
}

/**** Calculate Contract Cancellation Refund ****/
export interface CalculateContractCancellationRefundStartAction extends ContractAction {}

export interface CalculateContractCancellationRefundSuccessAction extends ContractAction {
  readonly refund: ContractCancellationModel;
}

export interface CalculateContractCancellationRefundFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface AddEmailNotificationAction extends ContractAction {
  readonly email: string;
}

export interface RemoveEmailNotificationAction extends ContractAction {
  readonly email: string;
}

export interface SetOverrideAction extends ContractAction {
  readonly type: ContractActionType.SetOverride;
  readonly value: boolean;
}

export interface SetRefundByCheckAction extends ContractAction {
  readonly type: ContractActionType.SetRefundByCheck;
  readonly value: boolean;
}

export interface ContractCancellationStartAction extends ContractAction {}

export interface ContractCancellationSuccessAction extends ContractAction {}

export interface ContractCancellationFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface ContractCancellationFormResetAction extends ContractAction {}

/************* Update Contract ************/
export interface UpdateContractStartAction extends ContractAction {}

export interface UpdateContractSuccessAction extends ContractAction {}

export interface UpdateContractFailureAction extends ContractAction {
  readonly errorMessage: string;
}

/******** Get Renewal Contract ***********/
export interface GetRenewalContractStartAction extends ContractAction {}
export interface GetRenewalContractSuccessAction extends ContractAction {
  readonly contractVM: ContractViewModel;
}
export interface GetRenewalContractFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface GetRenewalOptionsStartAction extends ContractAction {
  readonly type: ContractActionType.GetRenewalOptionsStart;
  readonly id: number;
}
export interface GetRenewalOptionsSuccessAction extends ContractAction {
  readonly type: ContractActionType.GetRenewalOptionsSuccess;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly searchResult: PageViewModel<ProductCoverageViewModel>;
}
export interface GetRenewalOptionsFailureAction extends ContractAction {
  readonly type: ContractActionType.GetRenewalOptionsFailure;
  readonly errorMessage: string;
}

/******** Send Renewal Notice ***********/
export interface SendRenewalNoticeStartAction extends ContractAction {}
export interface SendRenewalNoticeSuccessAction extends ContractAction {}
export interface SendRenewalNoticeFailureAction extends ContractAction {
  readonly errorMessage: string;
}

/******** Activate Contract ***********/
export interface ActivateContractFailureAction extends ContractAction {}
export interface ActivateContractStartAction extends ContractAction {}
export interface ActivateContractSuccessAction extends ContractAction {
  readonly coeDate: Date;
}

/**** Contract NoRenew ****/
export interface NoRenewContractStartAction extends ContractAction {}

export interface NoRenewContractSuccessAction extends ContractAction {
  readonly currentNoRenewVM: NoRenewViewModel;
}

export interface NoRenewContractFailureAction extends ContractAction {
  readonly errorMessage: string;
}

/******** Get Contract NoRenew Reasons  ********/
export interface GetContractNoRenewReasonsStartAction extends ContractAction {}

export interface GetContractNoRenewReasonsSuccessAction extends ContractAction {
  readonly noRenewReasons: Array<OptionsViewModel>;
}

export interface GetContractNoRenewReasonsFailureAction extends ContractAction {
  readonly errorMessage: string;
}

/**** Remove NoRenew ****/
export interface RemoveNoRenewContractStartAction extends ContractAction {}

export interface RemoveNoRenewContractSuccessAction extends ContractAction {}

export interface RemoveNoRenewContractFailureAction extends ContractAction {
  readonly errorMessage: string;
}

export interface ContractNoRenewFormResetAction extends ContractAction {}

export interface ResetContractStateAction extends ContractAction {}

export interface SuspendContractAction extends ContractAction {
  readonly type: ContractActionType.SuspendContract;
}

export interface ResumeContractAction extends ContractAction {
  readonly type: ContractActionType.ResumeContract;
}

export interface VerifyAction extends ContractAction {
  readonly type: ContractActionType.Verify;
}

export interface SetContractMetadataAction extends ContractAction {
  readonly type: ContractActionType.SetContractMetadata;
  contractMetadata: ContractMetaDataViewModel;
}

export interface SetContractIdAction extends ContractAction {
  readonly type: ContractActionType.SetContractId;
  readonly contractId: number;
}

export interface SetIsOpenContractAction extends ContractAction {
  readonly type: ContractActionType.SetIsOpenContract;
  readonly isOpenContract: boolean;
}

/********** Search **********/
export interface SearchContractsInitAction extends Action {
  readonly type: ContractActionType.SearchContractsInit;
  readonly query: string | null;
}

export interface SearchContractsSuccessAction extends Action {
  readonly type: ContractActionType.SearchContractsSuccess;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly searchResult: PageViewModel<ContractSearchViewModel>;
}

export interface SearchContractsFailureAction extends Action {
  readonly type: ContractActionType.SearchContractsFailure;
  readonly errorMessage: string;
}

/********** SavingUSPS **********/
export interface SavingUSPSAction extends Action {
  readonly type: ContractActionType.SavingUSPS;
  readonly isSaving: boolean;
}

export interface SetShouldClearAction extends Action {
  readonly type: ContractActionType.SetShouldClear;
  readonly shouldClear: boolean;
}

/********** SearchRelatedContracts **********/
export interface SearchRelatedContractsInitAction extends Action {
  readonly type: ContractActionType.SearchRelatedContractsInit;
  readonly contractId: number;
}

export interface SearchRelatedContractsSuccessAction extends Action {
  readonly type: ContractActionType.SearchRelatedContractsSuccess;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly searchResult: PageViewModel<ContractSimpleViewModel>;
}

export interface SearchRelatedContractsFailureAction extends Action {
  readonly type: ContractActionType.SearchRelatedContractsFailure;
  readonly errorMessage: string;
}

export function getContractDetails(id?: number) {
  return async (dispatch: Dispatch<AppState>) => {
    if (id) {
      dispatch(getContractDetailsStart());
      try {
        const contractDetailsVM = await contractService.getContractDetails(id);
        await dispatch(setOpenContract(contractDetailsVM));
        await dispatch(getContractDetailsSuccess(contractDetailsVM));
        await dispatch(getContractFiles(id));
        return contractDetailsVM;
      } catch (e) {
        await dispatch(getContractDetailsFailure(e.toString()));
      }
    }
    return undefined;
  };
}

export function getContract(id?: number) {
  return async (dispatch: Dispatch<AppState>) => {
    if (id) {
      dispatch(getContractStart());
      try {
        const contractVM = await contractService.getContract(id);
        dispatch(getContractSuccess(contractVM));
        dispatch(actions.load(FormNames.PropertyInfoForm, contractVM));
      } catch (e) {
        dispatch(getContractFailure(e.toString()));
      }
    }
  };
}
export function getContractStart(): GetContractStartAction {
  return {
    type: ContractActionType.GetContractStart
  };
}
export function getContractSuccess(contractVM: ContractViewModel): GetContractSuccessAction {
  return {
    type: ContractActionType.GetContractSuccess,
    contractVM: contractVM
  };
}
export function getContractFailure(errorMessage: string): GetContractFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Unable to get contract. ' + errorMessage} />,
    ToastOptions.Error
  );
  return {
    type: ContractActionType.GetContractFailure,
    errorMessage: errorMessage
  };
}

export function getContactInitiators() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getContactInitiatorsStart());
    try {
      const contactInitiators = await contractService.getContactInitiator();
      dispatch(getContactInitiatorsSuccess(contactInitiators));
    } catch (e) {
      dispatch(getContactInitiatorsFailure(e.toString()));
    }
  };
}

export function getContactTypes() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getContactTypesStart());
    try {
      const contactTypes = await contractService.getContactTypes();
      dispatch(getContactTypesSuccess(contactTypes));
    } catch (e) {
      dispatch(getContactTypesFailure(e.toString()));
    }
  };
}

export function getContactMediums() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getContactMediumsStart());
    try {
      const contactMediums = await contractService.getContactMedium();
      dispatch(getContactMediumsSuccess(contactMediums));
    } catch (e) {
      dispatch(getContactMediumsFailure(e.toString()));
    }
  };
}

export function getPropertyTypes() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getPropertyTypesStart());
    try {
      const propertyTypes = await contractService.getPropertyType();
      dispatch(getPropertyTypesSuccess(propertyTypes));
    } catch (e) {
      dispatch(getPropertyTypesFailure(e.toString()));
    }
  };
}

export function getPropertySaleTypes() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getPropertySaleTypesStart());
    try {
      const propertySaleTypes = await contractService.getSaleType();
      dispatch(getPropertySaleTypesSuccess(propertySaleTypes));
    } catch (e) {
      dispatch(getPropertySaleTypesFailure(e.toString()));
    }
  };
}

export function getBillingOptions() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getBillingOptionsStart());
    try {
      const billingOptions = await contractService.getBillingOptions();
      dispatch(getBillingOptionsSuccess(billingOptions));
    } catch (e) {
      dispatch(getBillingOptionsFailure(e.toString()));
    }
  };
}

export function getAssetManagers() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getAssetManagersStart());
    try {
      const assetManagers: Array<OptionsViewModel> = await contractService.getAssetManagers();
      dispatch(getAssetManagersSuccess(assetManagers));
    } catch (e) {
      dispatch(getAssetManagersFailure(e.toString()));
    }
  };
}

export function searchRealEstateAgents(query: string, id: number) {
  return realEstateAgentsService.search([id], undefined, undefined, undefined, 1, 10, query);
}

export function searchEscrowOfficers(query: string, id: number) {
  return escrowService.searchOfficers(query, id);
}

export function searchTransactionCoordinators(query: string, id: number) {
  return transactionCoordinatorService.searchTransactionCoordinators(query, id);
}

export function loadInitAgentSuccess(
  dispatch: Dispatch<ContractState>,
  agent: RealEstateAgentViewModel,
  office?: RealEstateOfficeViewModel
) {
  if (agent) {
    dispatch(actions.change('affiliatesInfoForm.initAgentId', agent.id));
  }
  if (office) {
    dispatch(actions.change('.affiliatesInfoForm.initOfficeId', office.id));
  }
  return {
    type: ContractActionType.LoadInitiatingAgentSuccess,
    initAgent: agent,
    initOffice: office
  };
}
export function loadCoopAgentSuccess(
  dispatch: Dispatch<ContractState>,
  agent: RealEstateAgentViewModel,
  office?: RealEstateOfficeViewModel
) {
  if (agent) {
    dispatch(actions.change('.affiliatesInfoForm.coopAgentId', agent.id));
  }

  if (office) {
    dispatch(actions.change('.affiliatesInfoForm.coopOfficeId', office.id));
  }

  return {
    type: ContractActionType.LoadCooperatingAgentSuccess,
    coopAgent: agent,
    coopOffice: office
  };
}
export function loadRealEstateAgentSuccess(
  dispatch: Dispatch<ContractState>,
  agent: RealEstateAgentViewModel,
  office?: RealEstateOfficeViewModel
): LoadRealEstateAgentSuccessAction {
  agent.realEstateOffice = office;
  return {
    type: ContractActionType.LoadRealEstateAgentSuccess,
    realEstateAgent: agent
  };
}

export function loadBuyer(customer: CustomerViewModel, shouldUpdateContract?: boolean) {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    const storeState = getStore();
    const contract: ContractDetailsViewModel = {
      ...storeState.contract.contractDetailsVM!,
      buyerId: customer.id
    };
    if (contract.id && shouldUpdateContract) {
      // this should only happen on the contract details page
      dispatch(updateContract(contract.id, { buyerId: customer.id })).then(() => {
        dispatch(sendDefaultConfirmationEmails(contract.id));
      });
    }
    dispatch(getEmployeeDiscount(customer.id));
    dispatch(actions.change(FormNames.CustomerInfoForm + '.buyerId', customer.id));
    dispatch(loadBuyerSuccess(customer));
  };
}

export function loadBuyerSuccess(customer: CustomerViewModel): LoadBuyerCustomerAction {
  return {
    type: ContractActionType.LoadBuyerCustomer,
    buyerCustomer: customer
  };
}

export function loadSeller(customer: CustomerViewModel, shouldUpdateContract?: boolean) {
  return (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    const storeState = getStore();
    const contract: ContractDetailsViewModel = {
      ...storeState.contract.contractDetailsVM!,
      sellerId: customer.id
    };
    if (contract.id && shouldUpdateContract) {
      // this should only happen on the contract details page
      dispatch(updateContract(contract.id, { sellerId: customer.id })).then(() => {
        dispatch(sendDefaultConfirmationEmails(contract.id));
      });
    }
    dispatch(actions.change(FormNames.CustomerInfoForm + '.sellerId', customer.id));
    dispatch(loadSellerSuccess(customer));
  };
}

export function loadSellerSuccess(customer: CustomerViewModel): LoadSellerCustomerAction {
  return {
    type: ContractActionType.LoadSellerCustomer,
    sellerCustomer: customer
  };
}

export function loadPropertyManager(propertyManager: PropertyManagerSearchViewModel): LoadPropertyManagerAction {
  return {
    type: ContractActionType.LoadPropertyManager,
    propertyManager: propertyManager
  };
}

export function loadEscrowOfficer(escrowOfficerVM: EscrowOfficerViewModel): LoadEscrowOfficerAction {
  return {
    type: ContractActionType.LoadEscrowOfficer,
    escrowOfficer: escrowOfficerVM
  };
}

export function loadTransactionCoordinator(
  transactionCoordinatorVM: TransactionCoordinatorViewModel
): LoadTransactionCoordinatorAction {
  return {
    type: ContractActionType.LoadTransactionCoordinator,
    transactionCoordinator: transactionCoordinatorVM
  };
}

export function loadTransactionCoordinatorIntoEscrowInfo() {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const tc = getState().transactionCoordinator.transactionCoordinatorVM;
    if (!tc) {
      return;
    }
    dispatch(loadTransactionCoordinator(tc));
  };
}

export function clearTransactionCoordinator(): ClearTransactionCoordinatorAction {
  return {
    type: ContractActionType.ClearTransactionCoordinator
  };
}

export function loadEscrowOffice(escrowOfficeVM: EscrowOfficeViewModel): LoadEscrowOfficeAction {
  return {
    type: ContractActionType.LoadEscrowOffice,
    escrowOffice: escrowOfficeVM
  };
}

// Actions for each action type declared here.
// They should all return an action object.

/************* Save ***********/
export function savePropertyInfo(model: ContractViewModel) {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    let promises: any = [];
    var contractVM: ContractViewModel = {
      ...model,
      mailReturned: model.mailReturned ? 1 : 0
    };
    const storeState = getStore();
    if (
      storeState.contract.contractVM &&
      storeState.contract.contractVM.propertyType.id !== +contractVM.propertyType.id
    ) {
      promises.push(dispatch(clearProductChannelSelection())); // also clears product
      contractVM.product = {
        productGroupId: storeState.contract.contractVM.product.productGroupId,
        productChannelId: storeState.contract.contractVM.product.productChannelId
      } as ProductViewModel;
    }
    const s = (((storeState.contract.contractVM || ({} as ContractViewModel)).propertyAddress || {}).state || {})
      .abbreviation;
    if (s !== undefined && s !== ((model.propertyAddress || {}).state || {}).abbreviation) {
      promises.push(dispatch(clearCoverageStore()));
    }
    promises.push(
      dispatch({ type: ContractActionType.SavePropertyInfo, contractVM: contractVM } as SavePropertyInfoAction)
    );
    await Promise.all(promises);
  };
}

/********** Search **********/
export const searchContracts = (
  pageNumber: number,
  pageSize: number,
  query: string | null,
  orderBy: string,
  orderDirection: string
) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(searchContractsInit(query));
    try {
      let searchResult: PageViewModel<ContractSearchViewModel> = await contractService.searchContracts(
        query,
        undefined,
        pageNumber,
        pageSize,
        orderBy,
        orderDirection
      );
      dispatch(searchContractsSuccess(pageNumber, pageSize, searchResult));
    } catch (e) {
      dispatch(searchContractsFailure(e.toString()));
    }
  };
};

export function searchContractsInit(query: string | null): SearchContractsInitAction {
  return {
    query: query,
    type: ContractActionType.SearchContractsInit
  };
}

export function searchContractsFailure(errorMessage: string): SearchContractsFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to search contracts. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: ContractActionType.SearchContractsFailure
  };
}

export function searchContractsSuccess(
  pageNumber: number,
  pageSize: number,
  result: PageViewModel<ContractSearchViewModel>
): SearchContractsSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchResult: result,
    type: ContractActionType.SearchContractsSuccess
  };
}

export function setOrderType(id: number) {
  return (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    const orderType =
      id === 1 ? ContractOrderType.DTC : id === 2 ? ContractOrderType.RealtyEscrow : ContractOrderType.Invalid;
    dispatch({
      type: ContractActionType.SaveOrderType,
      orderType: orderType
    } as SaveOrderTypeAction);
  };
}

export function saveAffiliatesInfo(model: ContractViewModel): SaveAffiliatesInfoAction {
  var contractVM: ContractViewModel = {
    ...model
  };
  return {
    type: ContractActionType.SaveAffiliatesInfo,
    contractVM: contractVM
  };
}
export function saveCustomerInfo(model: ContractViewModel): SaveCustomerInfoAction {
  var contractVM: ContractViewModel = {
    ...model
  };
  return {
    type: ContractActionType.SaveCustomerInfo,
    contractVM: contractVM
  };
}
export function saveEscrowInfo(model: ContractViewModel): SaveEscrowInfoAction {
  var contractVM: ContractViewModel = {
    ...model
  };
  return {
    type: ContractActionType.SaveEscrowInfo,
    contractVM: contractVM
  };
}

export function saveCoeDate(coeDate: Date): SaveCoeDateAction {
  return {
    type: ContractActionType.SaveCoeDate,
    coeDate: coeDate
  };
}

export function saveContract(model: ContractViewModel, callback?: Function) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(saveContractStart());
    try {
      if (
        model.transactionCoordinatorIds &&
        model.transactionCoordinatorIds.length > 0 &&
        model.transactionCoordinatorIds[0]
      ) {
        model.transCoordId = model.transactionCoordinatorIds[0].id;
        var transCoords = model.transactionCoordinatorIds.slice(1);
        transCoords.forEach(tc => {
          model.contractEntities.push({
            id: 0,
            contractId: 0,
            transactionCoordinatorId: tc.id
          });
        });
      }
      if (model.additionalRealEstateAgentIds && model.additionalRealEstateAgentIds.length > 0) {
        model.additionalRealEstateAgentIds.forEach(ra => {
          model.contractEntities.push({
            id: 0,
            contractId: 0,
            realEstateAgentId: ra
          });
        });
      }

      let contractVM = { ...model } as ContractViewModel;
      if (model.totalPrice < 0) {
        contractVM.totalPrice = 0;
      }
      const newContract: ContractViewModel = await contractService.saveContract(contractVM);
      var newContractId = newContract.id;
      var newContractAn = newContract.contractAn;
      dispatch(saveContractSuccess(newContractId, newContractAn));
      if (callback) {
        callback(newContractId);
      }
    } catch (e) {
      dispatch(saveContractFailure(e.message));
    }
  };
}

export function saveContractStart(): SaveContractStartAction {
  return {
    type: ContractActionType.SaveContractStart,
    message: 'Saving Contract Start'
  };
}

export function saveContractSuccess(contractId: number, contractAn: string): SaveContractSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Contract: ${contractAn} Saved Successfully`} />,
    ToastOptions.Success
  );

  return {
    type: ContractActionType.SaveContractSuccess,
    message: 'Contract Saved Successfully',
    contractId: contractId
  };
}

export function saveContractFailure(errorMessage: string): SaveContractFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage} />, ToastOptions.Error);
  return {
    type: ContractActionType.SaveContractFailure,
    errorMessage: errorMessage
  };
}

/************ Get ************/

export function getContractDetailsStart(): GetContractDetailsStartAction {
  return {
    type: ContractActionType.GetContractDetailsStart
  };
}
export function getContractDetailsSuccess(
  contractDetailsVM: ContractDetailsViewModel
): GetContractDetailsSuccessAction {
  return {
    type: ContractActionType.GetContractDetailsSuccess,
    contractDetailsVM: contractDetailsVM
  };
}
export function getContractDetailsFailure(errorMessage: string): GetContractDetailsFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Unable to get contract details. ' + errorMessage} />,
    ToastOptions.Error
  );
  return {
    type: ContractActionType.GetContractDetailsFailure,
    errorMessage: errorMessage
  };
}
export function clearContractDetails(): ClearContractDetailsAction {
  return {
    type: ContractActionType.ClearContractDetails
  };
}

export function getContactTypesStart() {
  return {
    type: ContractActionType.GetContactTypesStart
  };
}

export function getContactTypesSuccess(contactTypes: Array<OptionsViewModel>) {
  return {
    type: ContractActionType.GetContactTypesSuccess,
    contactTypes: contactTypes
  };
}

export function getContactTypesFailure(errorMessage: string) {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get contact types'} />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.GetContactTypesFailure,
    errorMessage: errorMessage
  };
}

export function getContactMediumsStart() {
  return {
    type: ContractActionType.GetContactMediumsStart
  };
}

export function getContactMediumsSuccess(contactMediums: Array<OptionsViewModel>) {
  return {
    type: ContractActionType.GetContactMediumsSuccess,
    contactMediums: contactMediums
  };
}

export function getContactMediumsFailure(errorMessage: string) {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get contact mediums'} />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.GetContactMediumsFailure,
    errorMessage: errorMessage
  };
}

export function getContactInitiatorsStart() {
  return {
    type: ContractActionType.GetContactInitiatorsStart
  };
}

export function getContactInitiatorsSuccess(contactInitiators: Array<OptionsViewModel>) {
  return {
    type: ContractActionType.GetContactInitiatorsSuccess,
    contactInitiators: contactInitiators
  };
}

export function getContactInitiatorsFailure(errorMessage: string) {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get contact initiators'} />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.GetContactInitiatorsFailure,
    errorMessage: errorMessage
  };
}

export function getPropertyTypesStart() {
  return {
    type: ContractActionType.GetPropertyTypesStart
  };
}

export function getPropertyTypesSuccess(propertyTypes: Array<PropertyTypeViewModel>) {
  return {
    type: ContractActionType.GetPropertyTypesSuccess,
    propertyTypes: propertyTypes
  };
}

export function getPropertyTypesFailure(errorMessage: string) {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get property types'} />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.GetPropertyTypesFailure,
    errorMessage: errorMessage
  };
}

export function getPropertySaleTypesStart() {
  return {
    type: ContractActionType.GetPropertySaleTypesStart
  };
}

export function getPropertySaleTypesSuccess(propertySaleTypes: Array<OptionsViewModel>) {
  return {
    type: ContractActionType.GetPropertySaleTypesSuccess,
    propertySaleTypes: propertySaleTypes
  };
}

export function getPropertySaleTypesFailure(errorMessage: string) {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get sale types'} />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.GetPropertySaleTypesFailure,
    errorMessage: errorMessage
  };
}

export function getBillingOptionsStart() {
  return {
    type: ContractActionType.GetBillingOptionsStart
  };
}

export function getBillingOptionsSuccess(billingOptions: Array<OptionsViewModel>) {
  return {
    type: ContractActionType.GetBillingOptionsSuccess,
    billingOptions: billingOptions
  };
}

export function getBillingOptionsFailure(errorMessage: string) {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get billing options'} />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.GetBillingOptionsFailure,
    errorMessage: errorMessage
  };
}

export function getAssetManagersStart() {
  return {
    type: ContractActionType.GetAssetManagersStart
  };
}

export function getAssetManagersSuccess(assetManagers: Array<OptionsViewModel>) {
  return {
    type: ContractActionType.GetAssetManagersSuccess,
    assetManagers: assetManagers
  };
}

export function getAssetManagersFailure(errorMessage: string) {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get asset managers'} />,
    ToastOptions.Error
  );
  return {
    type: ContractActionType.GetAssetManagersFailure,
    errorMessage: errorMessage
  };
}

export function lookupCustomer(value: string, id: number) {
  return customerService.searchCustomers(value, id);
}

export function lookupPropertyManager(value: string, id: number) {
  return propertyManagementService.searchPropertyManagers(value, id);
}

export function savePaymentInfo(id: number, payment?: PaymentViewModel, demandLetter?: boolean, callback?: () => void) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(savePaymentInfoStart());
    try {
      await contractService
        .finalizeContract(id, payment!.paymentAmountInitial!, demandLetter!)
        .then(async () => {
          await contractService.processPayment(id, payment!);
        })
        .then(() => {
          dispatch(savePaymentInfoSuccess(id));
          callback!();
        });
    } catch (e) {
      dispatch(savePaymentInfoFailure(e.message));
    }
  };
}

export function savePaymentInfoStart(): SavePaymentInfoStartAction {
  return {
    type: ContractActionType.SavePaymentInfoStart,
    message: 'Saving Payment Info Start'
  };
}

export function savePaymentInfoSuccess(contractId: number): SavePaymentInfoSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Contract: ${contractId} Updated and Finalized`} />,
    ToastOptions.Success
  );

  return {
    type: ContractActionType.SavePaymentInfoSuccess,
    contractId: contractId,
    message: 'Contract: ' + contractId + ' Updated and Finalized'
  };
}

export function savePaymentInfoFailure(errorMessage: string): SavePaymentInfoFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Contract could not be updated/finalized. Error: ' + errorMessage}
    />,
    ToastOptions.Error
  );
  return {
    type: ContractActionType.SavePaymentInfoFailure,
    errorMessage: 'Contract could not be updated. Error: ' + errorMessage
  };
}

export function clearBuyer(): ClearBuyerAction {
  return {
    type: ContractActionType.ClearBuyer
  };
}

export function clearSeller(): ClearSellerAction {
  return {
    type: ContractActionType.ClearSeller
  };
}

export function clearPropertyManager(): ClearPropertyManagerAction {
  return {
    type: ContractActionType.ClearPropertyManager
  };
}

export function clearInitAgent(): ClearInitAgentAction {
  return {
    type: ContractActionType.ClearInitAgent
  };
}

export function clearCoopAgent(): ClearCoopAgentAction {
  return {
    type: ContractActionType.ClearCoopAgent
  };
}

export function clearEscrowOfficer(): ClearEscrowOfficerAction {
  return {
    type: ContractActionType.ClearEscrowOfficer
  };
}

export function removeTxC(id: number): RemoveTxCAction {
  return {
    type: ContractActionType.RemoveTxC,
    id: id
  };
}

export function removeRealEstateAgent(index: number): RemoveRealEstateAgentAction {
  return {
    type: ContractActionType.RemoveRealEstateAgent,
    index: index
  };
}

export function removeAllContractRealEstateAgents(): RemoveRealEstateAgentAction {
  return {
    type: ContractActionType.RemoveRealEstateAgent,
    all: true,
    index: -1
  };
}

/********** SearchCommentsByContract **********/
export const searchCommentsByContract = (
  contractId: number,
  pageNumber: number,
  pageSize: number,
  query: string | null,
  filterBy?: string
) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(searchCommentsByContractInit(contractId));
    try {
      let searchResult: PageViewModel<CommentViewModel> = await contractService.searchContractComments(
        query,
        contractId,
        pageNumber,
        pageSize,
        filterBy
      );
      dispatch(searchCommentsByContractSuccess(pageNumber, pageSize, searchResult));
    } catch (e) {
      dispatch(searchCommentsByContractFailure(e.toString()));
    }
  };
};

export function searchCommentsByContractInit(contractId: number): SearchCommentsByContractInitAction {
  return {
    contractId: contractId,
    type: ContractActionType.SearchCommentsByContractInit
  };
}

export function searchCommentsByContractFailure(errorMessage: string): SearchCommentsByContractFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to search comments by contract. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: ContractActionType.SearchCommentsByContractFailure
  };
}

export function searchCommentsByContractSuccess(
  pageNumber: number,
  pageSize: number,
  result: PageViewModel<CommentViewModel>
): SearchCommentsByContractSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchResult: result,
    type: ContractActionType.SearchCommentsByContractSuccess
  };
}

export function selectComment(commentText: string): SelectCommentAction {
  return {
    type: ContractActionType.SelectComment,
    commentText: commentText
  };
}

export function startLoadingRealEstateAgent(
  agentType: ContractRealEstateAgentType,
  agentId: number
): GeneralLoadRealEstateAgentsAction {
  return {
    type: ContractActionType.ContractLoadRealEstateAgentsStart,
    agentType: agentType,
    agentId: agentId
  };
}

export function errorLoadingRealEstateAgent(
  agentType: ContractRealEstateAgentType,
  agentId: number,
  errorMessage: string
): GeneralLoadRealEstateAgentsAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage} />, ToastOptions.Error);

  return {
    type: ContractActionType.ContractLoadRealEstateAgentsFailure,
    agentType: agentType,
    agentId: agentId
  };
}

export function getRealEstateAgentInfo(
  id: number,
  onSuccessAction: (
    dispatch: Dispatch<ContractState>,
    agent: RealEstateAgentViewModel,
    office?: RealEstateOfficeViewModel
  ) => Action,
  agentType: ContractRealEstateAgentType
): (dispatch: Dispatch<ContractState>, getState: () => AppState) => Promise<Action> {
  return async (dispatch: Dispatch<ContractState>, getState: () => AppState) => {
    dispatch(startLoadingRealEstateAgent(agentType, id));

    const success = async (agent: RealEstateAgentViewModel) => {
      let office: RealEstateOfficeViewModel | undefined = undefined;
      if (agent.realEstateOffice && agent.realEstateOffice.id) {
        try {
          office = await realEstateOfficesService.get(agent.realEstateOffice.id);
        } catch (e) {
          office = undefined;
        }
      }
      return dispatch(onSuccessAction(dispatch, agent, office));
    };

    try {
      const agent = await realEstateAgentsService.get(id);
      return success(agent);
    } catch (err) {
      return dispatch(errorLoadingRealEstateAgent(agentType, id, err.message));
    }
  };
}

export function moveQuoteToContract(quote: QuoteViewModel): ConvertQuoteToContractAction {
  return {
    type: ContractActionType.ConvertQuoteToContract,
    quote: quote
  };
}

export function convertQuoteToContract(quote: QuoteViewModel) {
  return (dispatch: Dispatch<AppState>) => {
    if (!quote || !quote.product || !quote.product.productGroupId) {
      return;
    }
    const coverageState: CoverageState = pullCoverageStateFromEntity(quote);
    dispatch({
      ...loadCoverageState(coverageState),
      force: true
    });
    quote.yearBuilt = quote.yearBuilt ? '' + quote.yearBuilt : '';
    dispatch(actions.load(FormNames.PropertyInfoForm, quote));
    dispatch(convertQuotePreventReset(true));
    dispatch(moveQuoteToContract(quote));
  };
}

export function resetAffiliatesInfoStep() {
  return async (dispatch: Dispatch<AppState>) => {
    let promises: any[] = [];
    promises.push(dispatch(clearInitAgent()));
    promises.push(dispatch(clearCoopAgent()));
    promises.push(dispatch(removeAllContractRealEstateAgents()));
    return Promise.all(promises);
  };
}

export function resetPropertyInfoStep() {
  return async (dispatch: Dispatch<AppState>) => {
    return dispatch(actions.reset(FormNames.PropertyInfoForm));
  };
}

export function resetCustomerInfoStep() {
  return async (dispatch: Dispatch<AppState>) => {
    // remove buyer/seller from contract flow
    let promises = [];
    promises.push(dispatch(clearBuyer()));
    promises.push(dispatch(clearSeller()));
    return Promise.all(promises);
  };
}

export function resetEscrowOfficerInfoStep() {
  return async (dispatch: Dispatch<AppState>) => {
    // remove Escrow Officer/Office from contract flow
    dispatch(clearEscrowOfficer());
    dispatch(clearTransactionCoordinator());
  };
}

export function resetContract(clearPropertyInfo: boolean) {
  return (dispatch: Dispatch<AppState>) => {
    let promises: any[] = [];
    if (clearPropertyInfo) {
      promises.push(dispatch(actions.reset(FormNames.PropertyInfoForm)));
    }
    promises.push(dispatch(actions.reset(FormNames.AffiliatesInfoForm)));

    promises.push(dispatch(actions.reset(FormNames.CustomerInfoForm)));
    // TODO Remove references to escrow info form
    // dispatch(actions.reset('escrowInfoForm'));
    promises.push(dispatch(actions.reset(FormNames.PaymentForm)));

    promises.push(dispatch(resetAffiliatesInfoStep()));
    if (clearPropertyInfo) {
      promises.push(dispatch(resetPropertyInfoStep()));
    }
    promises.push(dispatch(clearCoverageStore()));
    promises.push(dispatch(actions.reset(FormNames.PriceAdjustmentForm)));
    promises.push(dispatch(actions.reset(FormNames.CoverageForm)));
    promises.push(dispatch(resetCustomerInfoStep()));
    promises.push(dispatch(resetContractState()));
    promises.push(dispatch(resetPaymentState()));
    return Promise.all(promises);
  };
}

export function resetContractState(): ResetContractStateAction {
  return {
    type: ContractActionType.ResetContractState
  };
}

/********** SaveContractFiles **********/
export const saveContractFiles = (contractId: number, fileVMs: FileViewModel[]) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(saveContractFilesStart());
    dispatch(setFileListLoading(true));
    try {
      const documentVMs = await documentService.saveFiles(fileVMs, undefined, contractId);
      dispatch(saveContractFilesSuccess(contractId, documentVMs));
      dispatch(getContractFiles(contractId));
      dispatch(setFileListLoading(false));
    } catch (e) {
      dispatch(saveContractFilesFailure(e as string));
      dispatch(setFileListLoading(false));
    }
  };
};

export function saveContractFilesStart(): SaveContractFilesStartAction {
  return {
    type: ContractActionType.SaveContractFilesStart
  };
}

export function saveContractFilesSuccess(
  contractId: number,
  documents: DocumentViewModel[]
): SaveContractFilesSuccessAction {
  return {
    type: ContractActionType.SaveContractFilesSuccess,
    contractId: contractId,
    documents: documents
  };
}

export function saveContractFilesFailure(errorMessage: string): SaveContractFilesFailureAction {
  return {
    type: ContractActionType.SaveContractFilesFailure,
    errorMessage: errorMessage
  };
}

/********** GetEdocTypes **********/
export function getEdocTypes() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getEdocTypesStart());
    try {
      const edocTypes = await documentService.getEdocTypes();
      dispatch(getEdocTypesSuccess(edocTypes));
    } catch (e) {
      dispatch(getEdocTypesFailure(e.toString()));
    }
  };
}

export function getEdocTypesStart() {
  return {
    type: ContractActionType.GetEdocTypesStart
  };
}

export function getEdocTypesSuccess(edocTypes: Array<OptionsViewModel>) {
  return {
    type: ContractActionType.GetEdocTypesSuccess,
    edocTypes: edocTypes
  };
}

export function getEdocTypesFailure(errorMessage: string) {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get edoc types'} />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.GetEdocTypesFailure,
    errorMessage: errorMessage
  };
}

/********** RemoveContractFile **********/
export const removeContractFile = (contractId: number, fileId: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(removeContractFileStart());
    dispatch(setFileListLoading(true));
    try {
      await documentService.removeFile(fileId);
      dispatch(removeContractFileSuccess(contractId, fileId));
      dispatch(getContractFiles(contractId));
      dispatch(setFileListLoading(false));
    } catch (e) {
      dispatch(removeContractFileFailure(e as string));
      dispatch(setFileListLoading(false));
    }
  };
};

export function removeContractFileStart(): RemoveContractFileStartAction {
  return {
    type: ContractActionType.RemoveContractFileStart
  };
}

export function removeContractFileSuccess(contractId: number, fileId: number): RemoveContractFileSuccessAction {
  return {
    type: ContractActionType.RemoveContractFileSuccess,
    contractId: contractId,
    fileId: fileId
  };
}

export function removeContractFileFailure(errorMessage: string): RemoveContractFileFailureAction {
  return {
    type: ContractActionType.RemoveContractFileFailure,
    errorMessage: errorMessage
  };
}

/********** GetContractFiles **********/
export function getContractFiles(contractId: number) {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(getContractFilesStart());
    documentService
      .getContractFiles(contractId)
      .then(documents => {
        dispatch(getContractFilesSuccess(contractId, documents));
      })
      .catch(err => {
        dispatch(getContractFilesFailure(err.errorMessage));
      });
  };
}
export function getContractFilesStart(): GetContractFilesStartAction {
  return {
    type: ContractActionType.GetContractFilesStart
  };
}
export function getContractFilesSuccess(
  contractId: number,
  documents: DocumentViewModel[]
): GetContractFilesSuccessAction {
  return {
    type: ContractActionType.GetContractFilesSuccess,
    contractId: contractId,
    documents: documents
  };
}
export function getContractFilesFailure(errorMessage: string): GetContractFilesFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get contract files ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.GetContractFilesFailure,
    errorMessage: errorMessage
  };
}

/**** Cancel Contract ****/
export function cancelContractOrder(id: number, orderCancellationModel: ContractOrderCancellationModel) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(cancelContractOrderStart());
    contractService
      .cancelContractOrder(id, orderCancellationModel)
      .then(() => {
        dispatch(cancelContractOrderSuccess());
      })
      .then(() => {
        dispatch(clearContractDetails());
      })
      .then(() => {
        dispatch(getContractDetails(id));
      })
      .catch(err => {
        dispatch(cancelContractOrderFailure(err.errorMessage));
      });
  };
}

export function cancelContractOrderStart(): CancelContractOrderStartAction {
  return {
    type: ContractActionType.CancelContractOrderStart
  };
}

export function cancelContractOrderSuccess(): CancelContractOrderSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Contract Canceled Successfully`} />,
    ToastOptions.Success
  );

  return {
    type: ContractActionType.CancelContractOrderSuccess,
    message: 'Contract Canceled Successfully'
  };
}

export function cancelContractOrderFailure(errorMessage: string): CancelContractOrderFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get cancel contract. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.CancelContractOrderFailure,
    errorMessage: errorMessage
  };
}

export function getContractCancellationReasons(id: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getContractCancellatinReasonsStart());
    try {
      const reasons: Array<OptionsViewModel> = await contractService.getContractCancellationReasons(id);
      dispatch(getContractCancellationReasonsSuccess(reasons));
    } catch (e) {
      dispatch(getContractCancellationReasonsFailure(e.toString()));
    }
  };
}

export function getContractCancellatinReasonsStart(): GetContractCancellationReasonsStartAction {
  return {
    type: ContractActionType.GetContractCancellationReasonsStart
  };
}

export function getContractCancellationReasonsSuccess(
  cancelReasons: Array<OptionsViewModel>
): GetContractCancellationReasonsSuccessAction {
  return {
    type: ContractActionType.GetContractCancellationReasonsSuccess,
    reasons: cancelReasons
  };
}

export function getContractCancellationReasonsFailure(
  errorMessage: string
): GetContractCancellationReasonsFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to get contract cancellation reasons. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.GetContractCancellationReasonsFailure,
    errorMessage: errorMessage
  };
}

export function setPaymentInfo(paymentInfo: ContractPaymentInfoModel): SetPaymentInfoAction {
  return {
    type: ContractActionType.SetPaymentInfo,
    paymentInfo: paymentInfo
  };
}

export function getPaymentInfo(contractId: number) {
  return async (dispatch: Dispatch<CoverageState>) => {
    dispatch(requestAction(() => contractService.getPaymentInfo(contractId), setPaymentInfo));
  };
}

export function setCancellationRefund(refundInfo: ContractCancellationModel): SetCancellationRefundAction {
  return {
    type: ContractActionType.SetCancellationRefund,
    refundInfo: refundInfo
  };
}

export function updateCancellationRefund(refundInfo: ContractCancellationModel) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    refundInfo.refundAmount =
      refundInfo.remainingBalance - refundInfo.adminFee - refundInfo.serviceCharges - refundInfo.pastDueServiceFee;
    return Promise.all([
      dispatch(setCancellationRefund(refundInfo)),
      dispatch(actions.change('cancelContractForm.refundAmount', refundInfo.refundAmount))
    ]);
  };
}

export function calculateContractCancellationRefund(id: number, calcParams: ContractCancellationModel) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(calculateContractCancellationRefundStart());
    try {
      const refund: ContractCancellationModel = await contractService.calculateCancellationRefund(id, calcParams);
      dispatch(actions.change('cancelContractForm.adminFee', refund.adminFee));
      dispatch(actions.change('cancelContractForm.serviceCharges', refund.serviceCharges));
      dispatch(actions.change('cancelContractForm.pastDueServiceFee', refund.pastDueServiceFee));
      dispatch(calculateContractCancellationRefundSuccess(refund));
    } catch (e) {
      dispatch(calculateContractCancellationRefundFailure(e.toString()));
    }
  };
}

export function calculateContractCancellationRefundStart(): CalculateContractCancellationRefundStartAction {
  return {
    type: ContractActionType.CalculateContractCancellationRefundStart
  };
}

export function calculateContractCancellationRefundSuccess(
  refund: any
): CalculateContractCancellationRefundSuccessAction {
  return {
    type: ContractActionType.CalculateContractCancellationRefundSuccess,
    refund: refund
  };
}

export function calculateContractCancellationRefundFailure(
  errorMessage: string
): CalculateContractCancellationRefundFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to get calculate contract cancellation refund. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.CalculateContractCancellationRefundFailure,
    errorMessage: errorMessage
  };
}

export function getContractCancellationNotifications(id: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getContractCancellationNotificationsStart());
    try {
      const notifications: Array<any> = await contractService.getContractCancellationNotifications(id);
      dispatch(getContractCancellationNotificationsSuccess(notifications));
    } catch (e) {
      dispatch(getContractCancellationNotificationsFailure(e.toString()));
    }
  };
}

export function getContractCancellationNotificationsStart(): GetContractCancellationNotificationsStartAction {
  return {
    type: ContractActionType.GetContractCancellationNotificationsStart
  };
}

export function getContractCancellationNotificationsSuccess(
  notifications: Array<ContractCancellationNotificationModel>
): GetContractCancellationNotificationsSuccessAction {
  return {
    type: ContractActionType.GetContractCancellationNotificationsSuccess,
    cancellationNotifications: notifications
  };
}

export function getContractCancellationNotificationsFailure(
  errorMessage: string
): GetContractCancellationNotificationsFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to get cancellation notifications for contract. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.GetContractCancellationNotificationsFailure,
    errorMessage: errorMessage
  };
}

export function addEmail(email: string) {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(actions.push('cancelContractForm.emailNotifications', email));
    dispatch(addEmailNotificationSuccess(email));
  };
}

export function addEmailNotificationSuccess(email: string): AddEmailNotificationAction {
  return {
    type: ContractActionType.AddEmailNotification,
    email: email
  };
}

export function removeEmail(email: string) {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(actions.filter('cancelContractForm.emailNotifications', (value: any) => value !== email));
    dispatch(removeEmailNotificationSuccess(email));
  };
}

export function removeEmailNotificationSuccess(email: string): RemoveEmailNotificationAction {
  return {
    type: ContractActionType.RemoveEmailNotification,
    email: email
  };
}

export function setOverride(val: boolean): SetOverrideAction {
  return {
    type: ContractActionType.SetOverride,
    value: val
  };
}

export function setRefundByCheck(val: boolean): SetRefundByCheckAction {
  return {
    type: ContractActionType.SetRefundByCheck,
    value: val
  };
}

export function cancelContract(id: number, formValue: ContractCancellationModel) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(cancelContractStart());
    try {
      const success: ContractCancellationModel = await contractService.cancelContract(id, formValue);
      return dispatch(cancelContractSuccess(success));
    } catch (e) {
      dispatch(cancelContractFailure(e.toString()));
      return;
    }
  };
}

export function cancelContractStart(): ContractCancellationStartAction {
  return {
    type: ContractActionType.ContractCancellationStart
  };
}

export function cancelContractSuccess(success: any): ContractCancellationSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Contract Cancelled Successfully`} />,
    ToastOptions.Success
  );

  return {
    type: ContractActionType.ContractCancellationSuccess
  };
}

export function cancelContractFailure(errorMessage: string): ContractCancellationFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to cancel contract. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.ContractCancellationFailure,
    errorMessage: errorMessage
  };
}

export function resetContractCancellationForm() {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(actions.reset('cancelContractForm'));
    dispatch(resetContractCancellation());
  };
}

export function resetContractCancellation(): ContractCancellationFormResetAction {
  return {
    type: ContractActionType.ContractCancellationFormReset
  };
}

export function updateContract(id: number, updateProps: Partial<ContractViewModel>) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(updateContractStart());
    try {
      const contract: ContractViewModel = await contractService.getContract(id);
      const newContract: ContractViewModel = { ...contract, ...updateProps };
      const success = await contractService.updateContract(id, newContract);
      return dispatch(updateContractSuccess(success));
    } catch (e) {
      return dispatch(updateContractFailure(e.toString()));
    }
  };
}

export function updateCoe(id: number, coeDate: Date) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(updateContractStart());
    try {
      const success = await contractService.updateCoe(id, coeDate);
      dispatch(updateContractSuccess(success));
    } catch (e) {
      dispatch(updateContractFailure(e.toString()));
    }
  };
}

export function updateContractStart(): UpdateContractStartAction {
  return {
    type: ContractActionType.UpdateContractStart
  };
}

export function updateContractSuccess(success: any): UpdateContractSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Contract Updated Successfully`} />,
    ToastOptions.Success
  );

  return {
    type: ContractActionType.UpdateContractSuccess
  };
}

export function updateContractFailure(errorMessage: string): UpdateContractFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to update contract. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.UpdateContractFailure,
    errorMessage: errorMessage
  };
}

/************ Get Renewal Options ************/
export const getRenewalOptions = (
  id: number,
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  orderDirection: string
) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getRenewalOptionsStart(id));
    try {
      let searchResult: PageViewModel<ProductCoverageViewModel> = await productService.getRenewalOptions(
        id,
        pageNumber,
        pageSize,
        orderBy,
        orderDirection
      );
      dispatch(getRenewalOptionsSuccess(pageNumber, pageSize, searchResult));
    } catch (e) {
      dispatch(getRenewalOptionsFailure(e.toString()));
    }
  };
};
export function getRenewalOptionsStart(id: number): GetRenewalOptionsStartAction {
  return {
    id: id,
    type: ContractActionType.GetRenewalOptionsStart
  };
}
export function getRenewalOptionsSuccess(
  pageNumber: number,
  pageSize: number,
  result: PageViewModel<ProductCoverageViewModel>
): GetRenewalOptionsSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchResult: result,
    type: ContractActionType.GetRenewalOptionsSuccess
  };
}
export function getRenewalOptionsFailure(errorMessage: string): GetRenewalOptionsFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Unable to get renewal options. ' + errorMessage} />,
    ToastOptions.Error
  );
  return {
    type: ContractActionType.GetRenewalOptionsFailure,
    errorMessage: errorMessage
  };
}

/************ Get Renewal Contract ************/
export function getRenewalContract(id?: number) {
  return async (dispatch: Dispatch<AppState>) => {
    if (id) {
      dispatch(getRenewalContractStart());
      try {
        let contractVM = await contractService.getRenewalContract(id);
        if (!contractVM.yearBuilt) {
          contractVM.yearBuilt = undefined;
        }
        if (contractVM.buyerId > 0) {
          dispatch(getById<CustomerViewModel>(contractVM.buyerId, getCustomerFromService)).then(customer => {
            if (!customer) {
              return;
            }
            dispatch(loadBuyer(customer));
          });
        }
        if (contractVM.sellerId > 0) {
          dispatch(getById<CustomerViewModel>(contractVM.sellerId, getCustomerFromService)).then(customer => {
            if (!customer) {
              return;
            }
            dispatch(loadSeller(customer));
          });
        }
        if (contractVM.propertyManagerId && contractVM.propertyManagerId > 0) {
          dispatch(getById<PropertyManagerViewModel>(contractVM.propertyManagerId, getPropertyManagerFromService)).then(
            propertyManagerVM => {
              if (!propertyManagerVM) {
                return;
              }
              let propertyManager = {
                ...propertyManagerVM,
                phone: propertyManagerVM.directPhone,
                officeId: propertyManagerVM.propMgmtOffice ? propertyManagerVM.propMgmtOffice!.id : null,
                officeName: propertyManagerVM.propMgmtOffice ? propertyManagerVM.propMgmtOffice!.value : null,
                city:
                  propertyManagerVM.address && propertyManagerVM.address.city ? propertyManagerVM.address!.city : null
              } as PropertyManagerSearchViewModel;
              dispatch(loadPropertyManager(propertyManager));
            }
          );
        }
        let product = contractVM.product;
        let coverageOptions = contractVM.coverageOptions;
        let sellerCoverage = contractVM.sellerCoverage;
        const coverageState: CoverageState = {
          ...initialCoverageState,
          selectedProductGroup: {
            id: product.productGroupId
          } as ProductGroupViewModel,
          selectedProductChannel: {
            id: product.productChannelId
          } as ProductChannelViewModel,
          selectedProduct: product,
          selectedCoverageOptions: coverageOptions,
          sellerCoverage: sellerCoverage,
          selectedPriceAdjustment: contractVM.priceAdjustment,
          overridePriceAdjustmentAmount: contractVM.priceAdjustment.adjustmentAmount,
          selectedGroupId: product.productGroupId,
          selectedChannelId: product.productChannelId
        };
        if (!contractVM.productGroup) {
          contractVM.productGroup = { id: product.productGroupId, value: '' };
        }
        contractVM.productChannel = { id: product.productChannelId, value: '' };

        dispatch(loadCoverageState(coverageState));
        dispatch(getRenewalContractSuccess(contractVM));
        contractVM.yearBuilt = contractVM.yearBuilt ? '' + contractVM.yearBuilt : '';
        dispatch(actions.load(FormNames.PropertyInfoForm, contractVM));
      } catch (e) {
        dispatch(getRenewalContractFailure(e.toString()));
      }
    }
  };
}
export function getRenewalContractStart(): GetRenewalContractStartAction {
  return {
    type: ContractActionType.GetRenewalContractStart
  };
}
export function getRenewalContractSuccess(contractVM: ContractViewModel): GetRenewalContractSuccessAction {
  return {
    type: ContractActionType.GetRenewalContractSuccess,
    contractVM: contractVM
  };
}
export function getRenewalContractFailure(errorMessage: string): GetRenewalContractFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Unable to get renewal contract. ' + errorMessage} />,
    ToastOptions.Error
  );
  return {
    type: ContractActionType.GetRenewalContractFailure,
    errorMessage: errorMessage
  };
}

/************ Send Renewal Notice ************/
export function sendRenewalNotice(id?: number) {
  return async (dispatch: Dispatch<AppState>) => {
    if (id) {
      dispatch(sendRenewalNoticeStart());
      try {
        await contractService.sendRenewalNotice(id);
        dispatch(sendRenewalNoticeSuccess());
      } catch (e) {
        dispatch(sendRenewalNoticeFailure(e.toString()));
      }
    }
  };
}
export function sendRenewalNoticeStart(): SendRenewalNoticeStartAction {
  return {
    type: ContractActionType.SendRenewalNoticeStart
  };
}
export function sendRenewalNoticeSuccess(): SendRenewalNoticeSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Renewal Notice Sent Successfully`} />,
    ToastOptions.Success
  );
  return {
    type: ContractActionType.SendRenewalNoticeSuccess
  };
}
export function sendRenewalNoticeFailure(errorMessage: string): SendRenewalNoticeFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Unable to send renewal notice. ' + errorMessage} />,
    ToastOptions.Error
  );
  return {
    type: ContractActionType.SendRenewalNoticeFailure,
    errorMessage: errorMessage
  };
}

export function activateContract(coeDate: Date) {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    dispatch(activateContractStart());
    try {
      const contractId: number = getStore().contract.contractDetailsVM!.id;
      await contractService.activateContract(contractId, coeDate);
      return dispatch(activateContractSuccess(coeDate));
    } catch (e) {
      dispatch(activateContractFailure());
      return;
    }
  };
}

export function activateContractFailure(): ActivateContractFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to activate contract'} />,
    ToastOptions.Error
  );
  return {
    type: ContractActionType.ActivateContractFailure
  };
}

export function activateContractStart(): ActivateContractStartAction {
  return {
    type: ContractActionType.ActivateContractStart
  };
}

export function activateContractSuccess(coeDate: Date): ActivateContractSuccessAction {
  toast.error(
    <ToastComponent type={ToastType.Success} message={'Contract activation successful'} />,
    ToastOptions.Success
  );
  return {
    type: ContractActionType.ActivateContractSuccess,
    coeDate: coeDate
  };
}
/**** Contract NoRenew ****/
export function noRenewContract(id: number, noRenewVMSave: NoRenewViewModel) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(NoRenewContractStart());
    try {
      const contract: ContractViewModel = await contractService.getContract(id);
      const newContract: ContractViewModel = { ...contract, noRenew: { ...noRenewVMSave } };
      // ", noRenew: { ...noRenewVMSave }" Same as using newContract.noRenew = noRenewVMSave
      await contractService.updateContract(id, newContract);
      return dispatch(NoRenewContractSuccess(noRenewVMSave));
    } catch (e) {
      dispatch(NoRenewContractFailure(e.toString()));
      return;
    }
  };
}

export function NoRenewContractStart(): NoRenewContractStartAction {
  return {
    type: ContractActionType.ContractNoRenewStart
  };
}

export function NoRenewContractSuccess(noRenewVMSaved: NoRenewViewModel): NoRenewContractSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Non-Renewal information has been saved successfully`} />,
    ToastOptions.Success
  );
  return {
    type: ContractActionType.ContractNoRenewSuccess,
    currentNoRenewVM: noRenewVMSaved
  };
}

export function NoRenewContractFailure(errorMessage: string): NoRenewContractFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to mark contract as No Renew. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.ContractNoRenewFailure,
    errorMessage: errorMessage
  };
}

/**** Get Contract NoRenew Reasons  ******/
export function getContractNoRenewReasons() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getContractNoRenewReasonsStart());
    try {
      const reasons: Array<OptionsViewModel> = await contractService.getNoRenewReasons();
      dispatch(getContractNoRenewReasonsSuccess(reasons));
    } catch (e) {
      dispatch(getContractNoRenewReasonsFailure(e.toString()));
    }
  };
}

export function getContractNoRenewReasonsStart(): GetContractNoRenewReasonsStartAction {
  return {
    type: ContractActionType.GetContractNoRenewReasonsStart
  };
}

export function getContractNoRenewReasonsSuccess(
  noRenewReasons: Array<OptionsViewModel>
): GetContractNoRenewReasonsSuccessAction {
  return {
    type: ContractActionType.GetContractNoRenewReasonsSuccess,
    noRenewReasons: noRenewReasons
  };
}

export function getContractNoRenewReasonsFailure(errorMessage: string): GetContractNoRenewReasonsFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to get contract no renew reasons. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.GetContractNoRenewReasonsFailure,
    errorMessage: errorMessage
  };
}

/**** Remove NoRenew ****/
export function removeNoRenew(id: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(RemoveNoRenewContractStart());
    try {
      const contract: ContractViewModel = await contractService.getContract(id);
      const newContract: ContractViewModel = { ...contract };
      newContract.noRenew.noRenewCd = 0;
      newContract.noRenew.noRenewDate = null;
      newContract.noRenew.noRenewDs = undefined;
      await contractService.updateContract(id, newContract);
      return dispatch(RemoveNoRenewContractSuccess());
    } catch (e) {
      dispatch(RemoveNoRenewContractFailure(e.toString()));
      return;
    }
  };
}

export function RemoveNoRenewContractStart(): RemoveNoRenewContractStartAction {
  return {
    type: ContractActionType.RemoveContractFileStart
  };
}

export function RemoveNoRenewContractSuccess(): RemoveNoRenewContractSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Non-Renewal information has been removed successfully`} />,
    ToastOptions.Success
  );
  return {
    type: ContractActionType.ContractRemoveNoRenewSuccess
  };
}

export function RemoveNoRenewContractFailure(errorMessage: string): RemoveNoRenewContractFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to remove no Renew from contract. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: ContractActionType.ContractRemoveNoRenewFailure,
    errorMessage: errorMessage
  };
}

/********** SearchRelatedContractsByContract **********/
export const searchRelatedContractsByContract = (customerId: number, pageNumber: number, pageSize: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(searchRelatedContractsByContractInit(customerId));
    try {
      let searchResult: PageViewModel<ContractSimpleViewModel> = await contractService.searchRelatedByContract(
        customerId,
        pageNumber,
        pageSize
      );
      dispatch(searchRelatedContractsByContractSuccess(pageNumber, pageSize, searchResult));
    } catch (e) {
      dispatch(searchRelatedContractsByContractFailure(e.toString()));
    }
  };
};

export function searchRelatedContractsByContractInit(contractId: number): SearchRelatedContractsInitAction {
  return {
    contractId: contractId,
    type: ContractActionType.SearchRelatedContractsInit
  };
}

export function searchRelatedContractsByContractFailure(errorMessage: string): SearchRelatedContractsFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to search related contracts by customer. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: ContractActionType.SearchRelatedContractsFailure
  };
}

export function searchRelatedContractsByContractSuccess(
  pageNumber: number,
  pageSize: number,
  result: PageViewModel<ContractSimpleViewModel>
): SearchRelatedContractsSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchResult: result,
    type: ContractActionType.SearchRelatedContractsSuccess
  };
}

function refreshContractHistoryAndDetails(id: number, dispatch: Dispatch<AppState>, getState: () => AppState) {
  const contractState = getState().contract;
  dispatch(getContractMetadata(id));
  dispatch(searchContractTerm('', contractState.historyPageSize, 1, 'termNumber', 'desc', id));
  return dispatch(getContractDetails(id));
}

export function setContractSuspend(): SuspendContractAction {
  return {
    type: ContractActionType.SuspendContract
  };
}
export function suspendContract(id: number) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return dispatch(
      requestAction(() => contractService.suspendContract(id), setContractSuspend, {
        hasToast: true,
        toastSuccessMessage: 'Contract Suspended Successfully.'
      })
    ).then(() => {
      return refreshContractHistoryAndDetails(id, dispatch, getState);
      // const contractState = getState().contract;
      // dispatch(getContractMetadata(id));
      // dispatch(searchContractTerm('', contractState.historyPageSize, 1, 'termNumber', 'desc', id));
      // return dispatch(getContractDetails(id));
    });
  };
}

export function setContractResume(): ResumeContractAction {
  return {
    type: ContractActionType.ResumeContract
  };
}

export function resumeContract(id: number) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return dispatch(
      requestAction(() => contractService.resumeContract(id), setContractResume, {
        hasToast: true,
        toastSuccessMessage: 'Contract Resumed Successfully.'
      })
    ).then(() => {
      return refreshContractHistoryAndDetails(id, dispatch, getState);
      // const contractState = getState().contract;
      // dispatch(getContractMetadata(id));
      // dispatch(searchContractTerm('', contractState.historyPageSize, 1, 'termNumber', 'desc', id));
      // return dispatch(getContractDetails(id));
    });
  };
}

export function setVerify(): VerifyAction {
  return {
    type: ContractActionType.Verify
  };
}

export function verify(id: number) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return dispatch(
      requestAction(() => contractService.verify(id), setVerify, {
        hasToast: true,
        toastSuccessMessage: 'Contract Info Verified Successfully.'
      })
    ).then(() => {
      dispatch(getContractMetadata(id));
      return dispatch(getContractDetails(id));
    });
  };
}

export function savingUSPS(isSaving: boolean): SavingUSPSAction {
  return {
    type: ContractActionType.SavingUSPS,
    isSaving: isSaving
  };
}

export function setUSPSStatus(contractId: number, uspsStatus: number) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    // dispatch(clearContractDetails()); // this is clearing/refreshing contract details page
    dispatch(savingUSPS(true));
    dispatch(
      requestAction(
        () => contractService.setUSPSStatus(contractId, uspsStatus),
        () => {
          return { type: '' };
        },
        {
          hasToast: true,
          toastSuccessMessage: 'Mail Returned Updated Successfully.'
        }
      )
    ).then(() => {
      dispatch(savingUSPS(false));
      dispatch(getContractMetadata(contractId));
      return dispatch(getContractDetails(contractId));
    });
  };
}

export function getContractMetadata(id: number) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return dispatch(
      requestAction(() => contractService.getContractMetadata(id), setContractMetadata, {
        hasToast: false,
        toastSuccessMessage: 'Contract Metadata Successfully.'
      })
    );
  };
}

export function setContractMetadata(contractMetadata: ContractMetaDataViewModel): SetContractMetadataAction {
  return {
    type: ContractActionType.SetContractMetadata,
    contractMetadata: contractMetadata
  };
}

export function cloneContract(contractId: string | number, useCurrentTerm?: boolean) {
  return async (dispatch: AppDispatcher) => {
    try {
      const contractDetailsVM: ContractDetailsViewModel = useCurrentTerm
        ? await contractService.getContractDetails(+contractId)
        : await contractService.getContractDetails(+contractId, 0);
      return await dispatch(loadContractDetailsIntoWorkflow(contractDetailsVM, useCurrentTerm));
    } catch (err) {
      return err;
    }
  };
}

export function loadContractDetailsIntoWorkflow(contractDetails: ContractDetailsViewModel, isOrderEdit?: boolean) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    let promises: any = [];
    const coverageState = pullCoverageStateFromEntity(contractDetails);
    // step 1
    promises.push(dispatch(doLoadPropertyInfo(pullPropertyInfoFromContractDetails(contractDetails))));
    // step 2
    promises.push(dispatch(loadCoverageState(coverageState)));
    // step 3
    promises.push(dispatch(loadCustomerInfo(contractDetails)));
    // step 4 affiliates
    promises.push(dispatch(doLoadAffiliatesWorkflow(contractDetails)));
    // step 5 Price Adjustments
    promises.push(
      Promise.all([
        dispatch(loadPriceAdjustmentWorkflow(contractDetails)),
        dispatch(
          actions.change(
            FormNames.PriceAdjustmentForm + '.priceAdjustmentAmount',
            contractDetails.priceAdjustmentAmount
          )
        ),
        dispatch(
          actions.change(FormNames.CoverageForm, {
            product: coverageState.selectedProduct
          })
        )
      ])
    );
    promises.push(dispatch(loadOtherContractDetails(contractDetails)));

    if (isOrderEdit) {
      // step 6
      promises.push(dispatch(saveToContractFlow(coverageState)));
      // step 7
      promises.push(dispatch(loadCustomerWorkflow(contractDetails)));
      // step 8 contractId
      promises.push(dispatch(setContractId(contractDetails.id)));
      promises.push(dispatch(setOpenContract(contractDetails)));
    }
    // dispatch(moveQuoteToContract({} as QuoteViewModel));
    return Promise.all(promises);
  };
}

function setOpenContract(contractDetails: ContractDetailsViewModel) {
  return async (dispatch: AppDispatcher) => {
    if (contractDetails.paymentTotalAmount !== undefined && contractDetails.ctcPriceAmount !== undefined) {
      var isOpenContract = (contractDetails.termNumber === 0 &&
        contractDetails.paymentTotalAmount < contractDetails.ctcPriceAmount) as boolean;
      var promise = await dispatch(setIsOpenContract(isOpenContract));
      return promise;
    }
    return;
  };
}

function setIsOpenContract(isOpenContract: boolean): SetIsOpenContractAction {
  return {
    type: ContractActionType.SetIsOpenContract,
    isOpenContract: isOpenContract
  };
}

function loadOtherContractDetails(details: ContractDetailsViewModel): LoadOtherContractInfoAction {
  return {
    type: ContractActionType.LoadOtherContractInfo,
    totalPrice: details.termPrice
      ? details.termPrice.totalPrice
      : +(details.originalPrice || 0) + details.priceAdjustmentAmount,
    id: details.id
  };
}

function setContractId(contractId: number): SetContractIdAction {
  return {
    type: ContractActionType.SetContractId,
    contractId: contractId
  };
}

function loadPriceAdjustmentWorkflow(
  details: ContractDetailsViewModel | ContractViewModel
): LoadPriceAdjustmentWorkflowAction {
  return {
    type: ContractActionType.LoadPriceAdjustmentWorkflow,
    priceAdjustment: details.priceAdjustment,
    priceAdjustmentAmount: Math.abs(details.priceAdjustmentAmount), // this should always be positive, but for whaterver reason they store it as a negative number on the contract
    priceAdjustmentNotes: details.priceAdjustmentNotes
  };
}

export function clearPriceAdjustmentFromVM() {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const state = getState();
    const contract = {
      ...state.contract.contractVM,
      priceAdjustment: {},
      priceAdjustmentAmount: 0,
      priceAdjustmentNotes: ''
    } as ContractViewModel;
    dispatch(loadPriceAdjustmentWorkflow(contract));
  };
}

function doLoadPropertyInfo(propertyInfo: PropertyInfoForm) {
  return async (dispatch: AppDispatcher) => {
    return Promise.all([
      dispatch(actions.change(FormNames.PropertyInfoForm, propertyInfo)),
      dispatch(loadPropertyInfo(propertyInfo))
    ]);
  };
}

function loadPropertyInfo(propertyInfo: PropertyInfoForm): LoadPropertyWorkflowAction {
  return {
    type: ContractActionType.LoadPropertyWorkFlow,
    propertyInfo: propertyInfo
  };
}

function loadCustomerInfo(details: ContractDetailsViewModel) {
  return async (dispatch: AppDispatcher) => {
    let promises = [];
    if (details.buyerId > 0) {
      promises.push(
        dispatch(getById<CustomerViewModel>(details.buyerId, getCustomerFromService)).then(customer => {
          if (!customer) {
            return;
          }
          return dispatch(loadBuyer(customer));
        })
      );
    }
    if (details.sellerId > 0) {
      promises.push(
        dispatch(getById<CustomerViewModel>(details.sellerId, getCustomerFromService)).then(customer => {
          if (!customer) {
            return;
          }
          return dispatch(loadSeller(customer));
        })
      );
    }

    if (details.propertyManagerId > 0) {
      promises.push(
        dispatch(getById<PropertyManagerViewModel>(details.propertyManagerId, getPropertyManagerFromService)).then(
          propertyManagerVM => {
            if (!propertyManagerVM) {
              return;
            }
            let propertyManager = {
              ...propertyManagerVM,
              phone: propertyManagerVM.directPhone,
              officeId: propertyManagerVM.propMgmtOffice ? propertyManagerVM.propMgmtOffice.id : null,
              officeName: propertyManagerVM.propMgmtOffice ? propertyManagerVM.propMgmtOffice.value : null,
              city: (propertyManagerVM.address || {}).city ? propertyManagerVM.address!.city : null
            } as PropertyManagerSearchViewModel;
            dispatch(loadPropertyManager(propertyManager));
          }
        )
      );
    }
    return Promise.all(promises);
  };
}

function loadCustomerWorkflow(details: ContractDetailsViewModel): LoadCustomerWorkflowAction {
  return {
    type: ContractActionType.LoadCustomerWorkFlow,
    buyerId: details.buyerId,
    sellerId: details.sellerId,
    propertyManagerId: details.propertyManagerId
  };
}

function doLoadAffiliatesWorkflow(details: ContractDetailsViewModel) {
  return async (dispatch: AppDispatcher) => {
    let additionalRealEstateAgentIds: Array<number> = [];
    details.additionalREAgents.forEach(agent => {
      if (agent.id) {
        additionalRealEstateAgentIds.push(agent.id);
      }
    });
    return Promise.all([
      dispatch(
        actions.change(FormNames.AffiliatesInfoForm, {
          escrowOneTimeFax: details.escrowOneTimeFax,
          txCOneTimeFax: details.txCOneTimeFax,
          fileNumber: details.fileNumber,
          estimatedCoeDate: details.estimatedCoeDate
        })
      ),
      dispatch(
        actions.change(FormNames.AffiliatesInfoForm, {
          initAgentId: details.initAgentId,
          initOfficeId: details.initOfficeId,
          coopAgentId: details.coopAgentId,
          coopOfficeId: details.coopOfficeId,
          additionalRealEstateAgentIds: additionalRealEstateAgentIds,

          propertyManagerId: details.propertyManagerId,

          // Reset Escrow Info Section
          escrowOfficerId: details.escrowOfficerId,
          escrowId: details.escrowId,
          escrowOneTimeFax: details.escrowOneTimeFax,
          fileNumber: details.fileNumber,
          estimatedCoeDate: details.estimatedCoeDate,
          transCoordId: details.transCoordId,
          txCOneTimeFax: details.txCOneTimeFax
        })
      ),
      dispatch(loadAffiliatesWorkflow(details))
    ]);
  };
}

function loadAffiliatesWorkflow(details: ContractDetailsViewModel): LoadAffiliatesWorkflowAction {
  return {
    type: ContractActionType.LoadAffiliatesWorkflow,
    initAgent: details.initAgent,
    coopAgent: details.coopAgent,
    initOffice: details.initOffice,
    coopOffice: details.coopOffice,
    additionalAgents: (details.additionalREAgents as any) || [],
    propertyManager: details.propertyManager,
    escrowComments: details.escrowComments,
    escrowOfficer: details.escrowOfficer,
    escrowOffice: details.escrowOffice,
    transactionCoordinatorIds: details.transactionCoordinators ? details.transactionCoordinators : [],
    additionalRealEstateAgentIds: details.additionalRealEstateAgentIds,
    assetManagers: details.assetManager ? [details.assetManager] : [],
    escrowOneTimeFax: details.escrowOneTimeFax,
    txCOneTimeFax: details.txCOneTimeFax,
    fileNumber: details.fileNumber,
    estimatedCoeDate: details.estimatedCoeDate
  };
}

export function setShouldClear(shouldClear: boolean): SetShouldClearAction {
  return {
    type: ContractActionType.SetShouldClear,
    shouldClear: shouldClear
  };
}



// WEBPACK FOOTER //
// ./src/contract/contract.actions.tsx